/**
 * The words of the access screens (0.10.0): client picker, operator panel,
 * invitations, tenant roles, the enforcement preview, API keys and where a
 * site publishes. Kept apart from dict.ts only to keep that file readable;
 * spread into the same two trees, so `dict.test.ts` checks them like the rest.
 */
import type { DictNode } from './dict.js'

export const accessIt: DictNode = {
  directory: {
    label: 'Persona di Security',
    search: 'Cerca per nome o email',
    help: 'Si sceglie solo tra le persone di Security: chi non c’è va prima creato su security.miglioresoftware.com.',
    empty: 'Nessuna persona di Security corrisponde alla ricerca',
    emptyHelp: 'Ogni utente del CMS deve prima esistere su security.miglioresoftware.com: crealo lì, poi torna qui.',
    unavailable: 'La rubrica di Security non è collegata al CMS: per ora non si può aggiungere nessuno.',
    alreadyMember: 'Già nel cliente',
    alreadyInvited: 'Già invitata',
    noAccessYet: 'Security le aprirà il CMS',
    willOpenAccess: 'Questa persona non ha ancora accesso al CMS: Security glielo apre quando confermi.',
    change: 'Cambia',
    required: 'Scegli una persona dalla rubrica di Security.',
    more: 'Altre {{count}} persone: restringi la ricerca.',
  },
  copy: {
    action: 'Copia',
    done: 'Copiato negli appunti',
    failed: 'Il browser non permette di copiare: seleziona il testo e copialo a mano.',
  },
  forms: {
    required: 'Campo obbligatorio.',
    tooLong: 'Troppo lungo.',
    domain: 'Scrivi solo il nome del dominio, per esempio www.example.com.',
    locales: 'Codici di lingua come it, en o en-us, separati da virgole.',
    defaultLocale: 'Scegli una delle lingue del sito.',
    email: 'Non sembra un indirizzo email valido.',
    invitationDays: 'Un numero intero da 1 a 90.',
    noteWithoutOrganization: 'La nota vale per il legame: indica anche l’organizzazione o togli la nota.',
    ownerOnly: 'Solo un proprietario può nominare un altro proprietario.',
    memberNeedsProject: 'Un membro senza ruolo nel cliente ha bisogno di almeno un progetto, altrimenti entra e non può fare niente.',
    scopesRequired: 'Scegli almeno un ambito.',
    scopesAll: 'Una chiave non può avere tutti gli ambiti: dalle solo quello che serve al programma che la usa.',
    scopesBeyond: 'La chiave non può avere ambiti che tu non hai.',
    date: 'Scegli una data.',
    datePast: 'La scadenza deve essere nel futuro.',
    dateTooFar: 'La scadenza può essere al massimo fra due anni.',
  },
  tenantSwitcher: {
    button: 'Cliente: {{name}}. Cambia cliente',
    none: 'Cliente',
    title: 'Scegli il cliente',
    intro: 'Tutto quello che fai da qui in poi (siti, contenuti, persone, chiavi) riguarda il cliente scelto. La scelta resta in questo browser.',
    search: 'Cerca un cliente',
    counts: '{{sites}} siti · {{projects}} progetti',
    operatorOnly: 'Lo vedi come operatore: per lavorarci serve un’appartenenza (un invito).',
    access: {
      home: 'Il tuo',
      member: 'Membro',
      operator: 'Solo operatore',
    },
    resetTitle: 'Sei tornato al tuo cliente',
    reset: 'Il cliente scelto in questo browser non ti riconosce più come membro (appartenenza revocata o disattivata). Se serve, chiedi un nuovo invito al suo amministratore.',
  },
  tenantRole: {
    owner: 'Proprietario',
    admin: 'Amministratore',
    member: 'Membro',
  },
  tenantRoleHelp: {
    owner: 'Tutto, compresa la creazione di siti e la nomina di altri proprietari.',
    admin: 'Tutto tranne la creazione di siti: persone, chiavi, struttura, contenuti, pubblicazione.',
    member: 'Nessun potere sul cliente: lavora solo sui progetti che gli assegni, con i ruoli di ciascuno.',
  },
  peopleTabs: {
    label: 'Sezioni delle persone',
    members: 'Membri',
    invitations: 'Inviti',
    preview: 'Prima di accendere i permessi',
  },
  memberRole: {
    title: 'Ruolo nel cliente',
    subtitle: 'Vale in tutto il cliente, sopra i ruoli dei singoli progetti. Una persona ha un solo ruolo alla volta.',
    label: 'Ruolo nel cliente',
    save: 'Salva il ruolo',
    saved: 'Ruolo aggiornato',
    ownerOnly: 'Nominare o declassare un proprietario è riservato ai proprietari: se non lo sei, il CMS rifiuterà.',
    lastOwnerTitle: 'È l’ultimo proprietario del cliente',
    lastOwnerHelp: 'Il CMS non lascia un cliente senza proprietari. Prima nomina proprietario un’altra persona (qui o con un invito con ruolo «Proprietario»), poi cambia il ruolo di questa.',
  },
  invitations: {
    intro: 'Invita una persona con la sua email: al primo accesso con l’accesso unico l’invito diventa la sua appartenenza al cliente.',
    new: 'Invita una persona',
    send: 'Invia l’invito',
    created: 'Invito creato: vale al primo accesso con quell’email',
    filter: 'Filtra per stato',
    all: 'Tutti',
    status: {
      pending: 'In attesa',
      accepted: 'Accettato',
      expired: 'Scaduto',
      revoked: 'Revocato',
    },
    empty: 'Nessun invito',
    emptyFiltered: 'Nessun invito in questo stato',
    emptyHelp: 'Invita chi deve lavorare su questo cliente, scegliendolo dalla rubrica di Security: non serve che sia già entrato qui.',
    acceptedAt: 'accettato il {{when}}',
    expiresAt: 'scade il {{when}}',
    revoke: 'Revoca',
    revokeTitle: 'Revocare l’invito?',
    revokeMessage: 'L’invito per {{email}} smette di valere subito. Se serve, potrai mandarne uno nuovo.',
    revoked: 'Invito revocato',
    dialogHelp: 'Il CMS non manda email: comunica tu alla persona di entrare con l’accesso unico, con l’indirizzo che ha su Security.',
    email: 'Email',
    role: 'Ruolo nel cliente',
    projects: 'Progetti a cui può accedere',
    noProjects: 'Non riesco a leggere i progetti del cliente: invita come amministratore o riprova più tardi.',
    projectRole: 'Ruolo sui progetti scelti',
    days: 'Validità dell’invito (giorni)',
    daysHelp: 'Da 1 a 90 giorni; di solito 14.',
  },
  preview: {
    intro: 'Cosa succederebbe a ogni persona conosciuta se i permessi per progetto venissero accesi adesso. Non cambia niente: è il controllo da leggere prima.',
    refresh: 'Aggiorna',
    safe: 'Si può accendere senza chiudere fuori nessuno che serve',
    unsafe: 'Accenderli adesso chiuderebbe fuori qualcuno che serve',
    safeHelp: 'Almeno una persona mantiene l’accesso completo e tu non perdi il tuo.',
    unsafeHelp: 'Nessuno manterrebbe l’accesso completo, oppure lo perderesti tu: assegna un ruolo o i progetti a chi risulta «Perde l’accesso» prima di accendere.',
    summary: {
      keepAll: 'Accesso completo',
      assigned: 'Solo i suoi progetti',
      loseAccess: 'Perde l’accesso',
    },
    meta: 'Permessi per progetto: {{enforcement}} · calcolata il {{generated}} · sessioni considerate dal {{since}}',
    enforcement: {
      enabled: 'accesi',
      disabled: 'spenti',
    },
    caller: 'Tu',
    entries: 'Le altre persone ({{count}})',
    empty: 'Nessun’altra persona conosciuta in questo cliente.',
    notes: 'Note',
    after: {
      all: 'Accesso completo',
      assigned: 'Solo i suoi progetti',
      none: 'Perde l’accesso',
    },
    roles: 'Ruoli: {{roles}}',
    projects: '{{count}} progetti assegnati',
    lastSeen: 'ultimo accesso {{when}}',
    neverSeen: 'mai visto con una sessione',
  },
  apiKeys: {
    title: 'Chiavi API',
    intro: 'Le chiavi con cui i programmi (script, automazioni, altri servizi) chiamano il CMS per questo cliente. Ognuna può solo quello che le dai.',
    new: 'Nuova chiave',
    create: 'Crea la chiave',
    empty: 'Nessuna chiave',
    emptyHelp: 'Crea una chiave per ogni programma: così puoi revocarne una senza fermare gli altri.',
    forbidden: 'Le chiavi API sono riservate a proprietari e amministratori del cliente.',
    status: {
      active: 'Attiva',
      revoked: 'Revocata',
      expired: 'Scaduta',
    },
    createdAt: 'creata il {{when}}',
    expiresAt: 'scade il {{when}}',
    revokedAt: 'revocata il {{when}}',
    noExpiry: 'senza scadenza',
    revoke: 'Revoca',
    revokeTitle: 'Revocare la chiave?',
    revokeMessage: 'La chiave «{{label}}» smette di funzionare dalla prossima richiesta. Il programma che la usa riceverà un errore finché non gliene dai una nuova.',
    revoked: 'Chiave revocata',
    label: 'Nome della chiave',
    labelHelp: 'Scrivi quale programma la usa, per esempio «Importazione newsletter».',
    access: 'Cosa può fare',
    mode: {
      role: 'Come un ruolo',
      scopes: 'Ambiti scelti uno per uno',
    },
    role: 'Ruolo',
    roleGrants: 'Ambiti: {{scopes}}',
    scopeNotYours: 'Non puoi darlo: non ce l’hai nemmeno tu.',
    scopeHelp: {
      'cms_tenancy_write': 'Persone, chiavi, siti e destinazioni del cliente.',
      'cms_content_read': 'Leggere contenuti, pagine e impostazioni.',
      'cms_content_write': 'Scrivere e correggere contenuti.',
      'cms_design_write': 'Tipi di contenuto, temi e impaginazione.',
      'cms_publish': 'Mettere online il sito.',
      'cms_media_write': 'Caricare e modificare file della libreria.',
      'cms_provision': 'Creare siti interi.',
      'cms_seo_read': 'Leggere i dati SEO.',
    },
    expiry: 'Scadenza',
    expiryOption: {
      '30': 'Fra 30 giorni',
      '90': 'Fra 90 giorni',
      '365': 'Fra un anno',
      never: 'Nessuna scadenza',
      custom: 'Scegli una data',
    },
    customDate: 'Scade alla fine del giorno',
    neverWarning: 'Una chiave senza scadenza vale finché qualcuno non la revoca: usala solo se sai chi se ne occuperà.',
    createdTitle: 'Chiave creata',
    secretWarningTitle: 'Copiala adesso: non la vedrai più',
    secretWarning: 'Il CMS conserva solo un’impronta della chiave, non la chiave. Chiusa questa finestra nessuno potrà mostrartela di nuovo: se la perdi, revocala e creane un’altra.',
    secretUsage: 'Il programma la manda nell’intestazione X-Api-Key di ogni richiesta. Non metterla nel codice né in un messaggio: conservala in un gestore di segreti.',
    replayedTitle: 'Questa chiave era già stata creata',
    replayed: 'La richiesta è arrivata due volte: la chiave esiste già e il suo segreto è stato mostrato solo la prima volta. Se non l’hai copiato, revocala e creane un’altra.',
    createdSummary: '«{{label}}» · ambiti: {{scopes}}',
    done: 'Ho copiato la chiave',
  },
  operator: {
    title: 'Operatore',
    intro: 'Tutti i clienti della piattaforma, le organizzazioni di Security legate a ciascuno e l’anteprima dei permessi. Essere operatore non dà accesso ai contenuti di un cliente.',
    forbiddenTitle: 'Riservato agli operatori della piattaforma',
    forbidden: 'Questa area è solo per gli operatori della piattaforma (PLATFORM_OPERATORS nella configurazione del CMS).',
    search: 'Cerca per nome, id o organizzazione',
    empty: 'Nessun cliente sulla piattaforma',
    counts: '{{projects}} progetti · {{sites}} siti',
    createdAt: 'creato il {{when}}',
    tenantStatus: {
      active: 'Attivo',
      suspended: 'Sospeso',
    },
    bindings: 'Organizzazioni legate',
    noBindings: 'Nessuna organizzazione legata: si entra solo con un invito.',
    bindingStatus: {
      active: 'Attivo',
      revoked: 'Revocato',
    },
    bind: 'Lega organizzazione',
    bindTitle: 'Lega un’organizzazione a {{tenant}}',
    bindHelp: 'Chi appartiene all’organizzazione di Security entra direttamente in questo cliente. Un’organizzazione può essere legata a un solo cliente alla volta.',
    externalTenantId: 'Id dell’organizzazione in Security',
    externalTenantIdHelp: 'Esattamente come Security lo mette nel token (tenant_id).',
    bindingNote: 'Nota (facoltativa)',
    bound: 'Organizzazione legata',
    revoke: 'Revoca',
    revokeTitle: 'Revocare il legame?',
    revokeMessage: 'Chi appartiene a {{organization}} non entrerà più in {{tenant}} dall’organizzazione, e le sessioni nate da questo legame si chiudono subito. Le appartenenze con invito restano.',
    revoked: 'Legame revocato: {{count}} sessioni chiuse',
    restore: 'Ripristina',
    restoreTitle: 'Ripristinare il legame?',
    restoreMessage: 'Chi appartiene a {{organization}} tornerà a entrare direttamente in {{tenant}}.',
    restored: 'Legame ripristinato',
    preview: 'Anteprima di accesso',
    previewTitle: 'Anteprima di accesso: {{tenant}}',
    open: 'Lavora su questo cliente',
  },
  newTenant: {
    title: 'Nuovo cliente',
    intro: 'Cliente, primo progetto, primo sito e invito del proprietario, creati insieme: o tutto o niente. Non va online niente.',
    stepsLabel: 'Passaggi',
    steps: {
      client: 'Cliente e progetto',
      site: 'Primo sito',
      owner: 'Proprietario',
    },
    stepHelp: {
      client: 'Il nome del cliente e del suo primo progetto (per esempio il marchio o il reparto).',
      site: 'Il dominio da cui il sito sarà servito e le sue lingue. Il DNS si collega dopo.',
      owner: 'Chi riceve il ruolo di proprietario, e l’organizzazione di Security da legare, se c’è già.',
    },
    tenantName: 'Nome del cliente',
    tenantNameHelp: 'Come compare nel selettore del cliente.',
    projectName: 'Nome del primo progetto',
    projectNameHelp: 'Siti e permessi si organizzano per progetto.',
    domain: 'Dominio del sito',
    domainHelp: 'Senza https:// e senza percorso.',
    ownerEmail: 'Email del proprietario',
    ownerEmailHelp: 'L’invito diventa la sua appartenenza al primo accesso con questa email.',
    bindingOptional: 'Organizzazione di Security (facoltativa)',
    bindingHelp: 'Se il cliente ha già un’organizzazione in Security, legala: chi ne fa parte entra direttamente. Puoi farlo anche dopo.',
    next: 'Avanti',
    back: 'Indietro',
    create: 'Crea il cliente',
    creating: 'Creazione…',
    created: 'Cliente creato',
    replayed: 'Il cliente esisteva già da questa stessa richiesta: nessun doppione',
    doneTitle: '{{name}} è pronto',
    doneIntro: 'Il cliente esiste, con il suo primo sito e l’invito del proprietario. Ecco cosa resta da fare.',
    summary: 'Riepilogo',
    tenantId: 'Id del cliente',
    siteId: 'Id del sito',
    owner: 'Proprietario',
    ownerInvited: '{{email}}, invito valido fino al {{when}}',
    binding: 'Organizzazione',
    noBinding: 'Nessuna, per ora',
    nextSteps: 'Prossimi passi',
    another: 'Crea un altro cliente',
    backToOperator: 'Torna all’elenco',
  },
  publishing: {
    tabsLabel: 'Sezioni delle impostazioni',
    tabGeneral: 'Generali',
    tabPublishing: 'Pubblicazione',
    intro: 'Dove va online il sito: il progetto Cloudflare Pages e i domini. L’account Cloudflare è sempre quello della piattaforma.',
    manageLink: 'Progetto Pages e domini →',
    target: 'Destinazione',
    targetIntro: 'Il progetto Cloudflare Pages su cui il CMS pubblica questo sito.',
    source: 'Da dove viene',
    sourceLabel: {
      site: 'Del sito',
      operator: 'Configurazione operatore',
      none: 'Nessuna',
    },
    sourceHelp: {
      site: 'La destinazione è un fatto del sito, registrato nel CMS: nessuna variabile da toccare.',
      operator: 'La destinazione viene ancora dalla configurazione dell’operatore (PAGES_TARGET_BINDINGS). «Crea o adotta progetto Pages» la registra nel sito, senza cambiare progetto.',
      none: 'Il sito non ha ancora un progetto: crealo per poterlo pubblicare.',
    },
    enabled: 'Pubblicazione',
    createProject: 'Crea o adotta progetto Pages',
    createProjectConfirm: 'Crea o adotta',
    createProjectHelp: 'Il CMS cerca il progetto del sito su Cloudflare Pages: se non c’è lo crea (caricamento diretto, ramo main, nessun repository), se c’è lo adotta. In entrambi i casi diventa la destinazione del sito. Chiamarlo due volte non crea doppioni.',
    createProjectHelpExisting: 'Il sito pubblica già su «{{name}}». Il CMS lo verifica su Cloudflare Pages e lo registra come destinazione del sito: nessun progetto nuovo, nessun doppione.',
    projectCreated: 'Progetto «{{name}}» creato',
    projectAdopted: 'Progetto «{{name}}» adottato',
    projectDetail: 'Indirizzo di Pages: {{subdomain}} · ramo di produzione: {{branch}}',
    domains: 'Domini',
    domainsIntro: 'Lo stato di ogni dominio del sito su Cloudflare Pages e il record DNS da creare. Il CMS non scrive nel DNS: lo fa chi gestisce la zona.',
    domainsNeedProject: 'Prima serve il progetto Pages',
    domainsNeedProjectHelp: 'Crea o adotta il progetto qui sopra: poi compaiono i domini con il loro stato.',
    domainsEmpty: 'Il sito non ha domini',
    addDomain: 'Aggiungi un dominio nelle impostazioni generali',
    addMore: 'Manca un dominio?',
    refresh: 'Aggiorna lo stato',
    status: {
      active: 'Attivo',
      pending: 'In attesa',
      error: 'Errore',
      not_attached: 'Non collegato',
    },
    attach: 'Collega a Pages',
    attachAgain: 'Collega di nuovo',
    attached: '{{domain}} collegato a Pages',
    cloudflareStatus: 'Stato secondo Cloudflare: {{status}}',
    cloudflareMessage: 'Messaggio di Cloudflare: {{message}}',
    dnsTitle: 'Record DNS da creare',
    dnsType: 'Tipo',
    dnsName: 'Nome',
    dnsContent: 'Destinazione',
    dnsHelp: 'Crealo nella zona DNS del dominio (Cloudflare o un altro provider), poi premi «Aggiorna lo stato»: il certificato arriva da solo, di solito in pochi minuti.',
    copyTarget: 'Copia destinazione',
    copyRecord: 'Copia record',
  },
  accessReason: {
    role: 'Ruolo di {{email}} nel cliente: {{role}}',
    invite: 'Invito a {{email}} come {{role}}',
    revokeInvitation: 'Revoca dell’invito a {{email}}',
    apiKey: 'Nuova chiave API «{{label}}»',
    revokeApiKey: 'Revoca della chiave API «{{label}}»',
    operatorTenant: 'Nuovo cliente {{name}} dal pannello operatore',
    bind: 'Legame dell’organizzazione {{organization}} al cliente {{tenant}}',
    revokeBinding: 'Revoca del legame fra {{organization}} e il cliente {{tenant}}',
    restoreBinding: 'Ripristino del legame fra {{organization}} e il cliente {{tenant}}',
    pagesProject: 'Progetto Cloudflare Pages del sito {{site}}',
    pagesDomain: 'Collegamento di {{domain}} al progetto Pages',
  },
}

export const accessEn: DictNode = {
  directory: {
    label: 'Person on Security',
    search: 'Search by name or email',
    help: 'You can only pick people who are on Security: anyone missing must first be created on security.miglioresoftware.com.',
    empty: 'No person on Security matches the search',
    emptyHelp: 'Every CMS user must first exist on security.miglioresoftware.com: create them there, then come back.',
    unavailable: 'The Security directory is not connected to the CMS: nobody can be added for now.',
    alreadyMember: 'Already in the client',
    alreadyInvited: 'Already invited',
    noAccessYet: 'Security will open the CMS to them',
    willOpenAccess: 'This person has no access to the CMS yet: Security opens it when you confirm.',
    change: 'Change',
    required: 'Pick a person from the Security directory.',
    more: '{{count}} more people: narrow the search.',
  },
  copy: {
    action: 'Copy',
    done: 'Copied to the clipboard',
    failed: 'The browser does not allow copying: select the text and copy it by hand.',
  },
  forms: {
    required: 'Required.',
    tooLong: 'Too long.',
    domain: 'Write the domain name only, for example www.example.com.',
    locales: 'Language codes such as it, en or en-us, separated by commas.',
    defaultLocale: 'Pick one of the site languages.',
    email: 'This does not look like a valid email address.',
    invitationDays: 'A whole number from 1 to 90.',
    noteWithoutOrganization: 'The note belongs to the binding: name the organization too, or remove the note.',
    ownerOnly: 'Only an owner can name another owner.',
    memberNeedsProject: 'A member without a client role needs at least one project, or they get in and can do nothing.',
    scopesRequired: 'Pick at least one scope.',
    scopesAll: 'A key cannot have every scope: give it only what the program using it needs.',
    scopesBeyond: 'The key cannot have scopes you do not have.',
    date: 'Pick a date.',
    datePast: 'The expiry must be in the future.',
    dateTooFar: 'The expiry can be at most two years away.',
  },
  tenantSwitcher: {
    button: 'Client: {{name}}. Change client',
    none: 'Client',
    title: 'Pick the client',
    intro: 'Everything you do from now on (sites, contents, people, keys) concerns the chosen client. The choice stays in this browser.',
    search: 'Search a client',
    counts: '{{sites}} sites · {{projects}} projects',
    operatorOnly: 'You see it as an operator: working on it takes a membership (an invitation).',
    access: {
      home: 'Yours',
      member: 'Member',
      operator: 'Operator only',
    },
    resetTitle: 'You are back on your own client',
    reset: 'The client chosen in this browser no longer recognizes you as a member (membership revoked or disabled). If needed, ask its administrator for a new invitation.',
  },
  tenantRole: {
    owner: 'Owner',
    admin: 'Administrator',
    member: 'Member',
  },
  tenantRoleHelp: {
    owner: 'Everything, including creating sites and naming other owners.',
    admin: 'Everything except creating sites: people, keys, structure, contents, publishing.',
    member: 'No power over the client: works only on the projects you assign, with each one’s roles.',
  },
  peopleTabs: {
    label: 'People sections',
    members: 'Members',
    invitations: 'Invitations',
    preview: 'Before turning permissions on',
  },
  memberRole: {
    title: 'Role in the client',
    subtitle: 'Applies to the whole client, above the roles on single projects. A person holds one role at a time.',
    label: 'Role in the client',
    save: 'Save the role',
    saved: 'Role updated',
    ownerOnly: 'Naming or demoting an owner is reserved to owners: if you are not one, the CMS will refuse.',
    lastOwnerTitle: 'This is the client’s last owner',
    lastOwnerHelp: 'The CMS never leaves a client without owners. First make someone else an owner (here or with an invitation with the “Owner” role), then change this person’s role.',
  },
  invitations: {
    intro: 'Invite a person by email: at their first single sign-on login the invitation becomes their membership of the client.',
    new: 'Invite a person',
    send: 'Send the invitation',
    created: 'Invitation created: it applies at the first login with that email',
    filter: 'Filter by status',
    all: 'All',
    status: {
      pending: 'Pending',
      accepted: 'Accepted',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    empty: 'No invitations',
    emptyFiltered: 'No invitations with this status',
    emptyHelp: 'Invite whoever has to work on this client, picking them from the Security directory: they do not need to have signed in here yet.',
    acceptedAt: 'accepted on {{when}}',
    expiresAt: 'expires on {{when}}',
    revoke: 'Revoke',
    revokeTitle: 'Revoke the invitation?',
    revokeMessage: 'The invitation for {{email}} stops working at once. You can send a new one if needed.',
    revoked: 'Invitation revoked',
    dialogHelp: 'The CMS sends no email: tell the person to sign in with single sign-on, with the address they have on Security.',
    email: 'Email',
    role: 'Role in the client',
    projects: 'Projects they can access',
    noProjects: 'The client’s projects cannot be read: invite as administrator or try again later.',
    projectRole: 'Role on the chosen projects',
    days: 'Invitation validity (days)',
    daysHelp: 'From 1 to 90 days; usually 14.',
  },
  preview: {
    intro: 'What would happen to every known person if project permissions were turned on now. It changes nothing: it is the check to read first.',
    refresh: 'Refresh',
    safe: 'It can be turned on without locking out anyone needed',
    unsafe: 'Turning them on now would lock out someone needed',
    safeHelp: 'At least one person keeps full access and you do not lose yours.',
    unsafeHelp: 'Nobody would keep full access, or you would lose it: give a role or projects to whoever shows “Loses access” before turning them on.',
    summary: {
      keepAll: 'Full access',
      assigned: 'Only their projects',
      loseAccess: 'Loses access',
    },
    meta: 'Project permissions: {{enforcement}} · computed on {{generated}} · sessions considered since {{since}}',
    enforcement: {
      enabled: 'on',
      disabled: 'off',
    },
    caller: 'You',
    entries: 'The other people ({{count}})',
    empty: 'No other known person in this client.',
    notes: 'Notes',
    after: {
      all: 'Full access',
      assigned: 'Only their projects',
      none: 'Loses access',
    },
    roles: 'Roles: {{roles}}',
    projects: '{{count}} projects assigned',
    lastSeen: 'last seen {{when}}',
    neverSeen: 'never seen with a session',
  },
  apiKeys: {
    title: 'API keys',
    intro: 'The keys programs (scripts, automations, other services) use to call the CMS for this client. Each one can do only what you give it.',
    new: 'New key',
    create: 'Create the key',
    empty: 'No keys',
    emptyHelp: 'Create one key per program: then you can revoke one without stopping the others.',
    forbidden: 'API keys are reserved to the client’s owners and administrators.',
    status: {
      active: 'Active',
      revoked: 'Revoked',
      expired: 'Expired',
    },
    createdAt: 'created on {{when}}',
    expiresAt: 'expires on {{when}}',
    revokedAt: 'revoked on {{when}}',
    noExpiry: 'no expiry',
    revoke: 'Revoke',
    revokeTitle: 'Revoke the key?',
    revokeMessage: 'The key “{{label}}” stops working from the next request. The program using it will get an error until you give it a new one.',
    revoked: 'Key revoked',
    label: 'Key name',
    labelHelp: 'Say which program uses it, for example “Newsletter import”.',
    access: 'What it can do',
    mode: {
      role: 'Like a role',
      scopes: 'Scopes picked one by one',
    },
    role: 'Role',
    roleGrants: 'Scopes: {{scopes}}',
    scopeNotYours: 'You cannot give it: you do not have it either.',
    scopeHelp: {
      'cms_tenancy_write': 'People, keys, sites and destinations of the client.',
      'cms_content_read': 'Read contents, pages and settings.',
      'cms_content_write': 'Write and fix contents.',
      'cms_design_write': 'Content types, themes and layout.',
      'cms_publish': 'Put the site online.',
      'cms_media_write': 'Upload and edit library files.',
      'cms_provision': 'Create whole sites.',
      'cms_seo_read': 'Read SEO data.',
    },
    expiry: 'Expiry',
    expiryOption: {
      '30': 'In 30 days',
      '90': 'In 90 days',
      '365': 'In a year',
      never: 'No expiry',
      custom: 'Pick a date',
    },
    customDate: 'Expires at the end of the day',
    neverWarning: 'A key without expiry works until someone revokes it: use it only if you know who will look after it.',
    createdTitle: 'Key created',
    secretWarningTitle: 'Copy it now: you will not see it again',
    secretWarning: 'The CMS keeps only a fingerprint of the key, not the key. Once this window is closed nobody can show it to you again: if you lose it, revoke it and create another.',
    secretUsage: 'The program sends it in the X-Api-Key header of every request. Do not put it in code or in a message: keep it in a secrets manager.',
    replayedTitle: 'This key had already been created',
    replayed: 'The request arrived twice: the key already exists and its secret was shown only the first time. If you did not copy it, revoke it and create another.',
    createdSummary: '“{{label}}” · scopes: {{scopes}}',
    done: 'I copied the key',
  },
  operator: {
    title: 'Operator',
    intro: 'Every client of the platform, the Security organizations bound to each and the permissions preview. Being an operator gives no access to a client’s contents.',
    forbiddenTitle: 'Reserved to platform operators',
    forbidden: 'This area is only for platform operators (PLATFORM_OPERATORS in the CMS configuration).',
    search: 'Search by name, id or organization',
    empty: 'No clients on the platform',
    counts: '{{projects}} projects · {{sites}} sites',
    createdAt: 'created on {{when}}',
    tenantStatus: {
      active: 'Active',
      suspended: 'Suspended',
    },
    bindings: 'Bound organizations',
    noBindings: 'No organization bound: people get in only with an invitation.',
    bindingStatus: {
      active: 'Active',
      revoked: 'Revoked',
    },
    bind: 'Bind organization',
    bindTitle: 'Bind an organization to {{tenant}}',
    bindHelp: 'Whoever belongs to the Security organization gets straight into this client. An organization can be bound to one client at a time.',
    externalTenantId: 'Organization id in Security',
    externalTenantIdHelp: 'Exactly as Security puts it in the token (tenant_id).',
    bindingNote: 'Note (optional)',
    bound: 'Organization bound',
    revoke: 'Revoke',
    revokeTitle: 'Revoke the binding?',
    revokeMessage: 'Members of {{organization}} will no longer get into {{tenant}} through the organization, and the sessions born under this binding close at once. Memberships from invitations stay.',
    revoked: 'Binding revoked: {{count}} sessions closed',
    restore: 'Restore',
    restoreTitle: 'Restore the binding?',
    restoreMessage: 'Members of {{organization}} will get straight into {{tenant}} again.',
    restored: 'Binding restored',
    preview: 'Access preview',
    previewTitle: 'Access preview: {{tenant}}',
    open: 'Work on this client',
  },
  newTenant: {
    title: 'New client',
    intro: 'Client, first project, first site and the owner’s invitation, created together: all or nothing. Nothing goes online.',
    stepsLabel: 'Steps',
    steps: {
      client: 'Client and project',
      site: 'First site',
      owner: 'Owner',
    },
    stepHelp: {
      client: 'The name of the client and of its first project (for example the brand or the department).',
      site: 'The domain the site will be served from and its languages. DNS is connected later.',
      owner: 'Who gets the owner role, and the Security organization to bind, if it already exists.',
    },
    tenantName: 'Client name',
    tenantNameHelp: 'As shown in the client picker.',
    projectName: 'First project name',
    projectNameHelp: 'Sites and permissions are organized by project.',
    domain: 'Site domain',
    domainHelp: 'Without https:// and without a path.',
    ownerEmail: 'Owner email',
    ownerEmailHelp: 'The invitation becomes their membership at the first login with this email.',
    bindingOptional: 'Security organization (optional)',
    bindingHelp: 'If the client already has an organization in Security, bind it: its members get straight in. You can also do it later.',
    next: 'Next',
    back: 'Back',
    create: 'Create the client',
    creating: 'Creating…',
    created: 'Client created',
    replayed: 'The client already existed from this same request: no duplicate',
    doneTitle: '{{name}} is ready',
    doneIntro: 'The client exists, with its first site and the owner’s invitation. Here is what is left to do.',
    summary: 'Summary',
    tenantId: 'Client id',
    siteId: 'Site id',
    owner: 'Owner',
    ownerInvited: '{{email}}, invitation valid until {{when}}',
    binding: 'Organization',
    noBinding: 'None, for now',
    nextSteps: 'Next steps',
    another: 'Create another client',
    backToOperator: 'Back to the list',
  },
  publishing: {
    tabsLabel: 'Settings sections',
    tabGeneral: 'General',
    tabPublishing: 'Publishing',
    intro: 'Where the site goes online: the Cloudflare Pages project and the domains. The Cloudflare account is always the platform’s.',
    manageLink: 'Pages project and domains →',
    target: 'Destination',
    targetIntro: 'The Cloudflare Pages project the CMS publishes this site to.',
    source: 'Where it comes from',
    sourceLabel: {
      site: 'The site’s own',
      operator: 'Operator configuration',
      none: 'None',
    },
    sourceHelp: {
      site: 'The destination is a fact of the site, recorded in the CMS: no variable to touch.',
      operator: 'The destination still comes from the operator configuration (PAGES_TARGET_BINDINGS). “Create or adopt Pages project” records it on the site, without changing project.',
      none: 'The site has no project yet: create it to be able to publish.',
    },
    enabled: 'Publishing',
    createProject: 'Create or adopt Pages project',
    createProjectConfirm: 'Create or adopt',
    createProjectHelp: 'The CMS looks for the site’s project on Cloudflare Pages: if it is missing it creates it (direct upload, main branch, no repository), if it exists it adopts it. Either way it becomes the site’s destination. Calling it twice creates no duplicates.',
    createProjectHelpExisting: 'The site already publishes to “{{name}}”. The CMS checks it on Cloudflare Pages and records it as the site’s destination: no new project, no duplicate.',
    projectCreated: 'Project “{{name}}” created',
    projectAdopted: 'Project “{{name}}” adopted',
    projectDetail: 'Pages address: {{subdomain}} · production branch: {{branch}}',
    domains: 'Domains',
    domainsIntro: 'The state of each site domain on Cloudflare Pages and the DNS record to create. The CMS never writes DNS: whoever runs the zone does.',
    domainsNeedProject: 'The Pages project comes first',
    domainsNeedProjectHelp: 'Create or adopt the project above: then the domains show up with their state.',
    domainsEmpty: 'The site has no domains',
    addDomain: 'Add a domain in the general settings',
    addMore: 'Missing a domain?',
    refresh: 'Refresh the state',
    status: {
      active: 'Active',
      pending: 'Pending',
      error: 'Error',
      not_attached: 'Not attached',
    },
    attach: 'Attach to Pages',
    attachAgain: 'Attach again',
    attached: '{{domain}} attached to Pages',
    cloudflareStatus: 'State according to Cloudflare: {{status}}',
    cloudflareMessage: 'Cloudflare says: {{message}}',
    dnsTitle: 'DNS record to create',
    dnsType: 'Type',
    dnsName: 'Name',
    dnsContent: 'Target',
    dnsHelp: 'Create it in the domain’s DNS zone (Cloudflare or another provider), then press “Refresh the state”: the certificate comes by itself, usually within minutes.',
    copyTarget: 'Copy target',
    copyRecord: 'Copy record',
  },
  accessReason: {
    role: 'Role of {{email}} in the client: {{role}}',
    invite: 'Invitation to {{email}} as {{role}}',
    revokeInvitation: 'Invitation to {{email}} revoked',
    apiKey: 'New API key “{{label}}”',
    revokeApiKey: 'API key “{{label}}” revoked',
    operatorTenant: 'New client {{name}} from the operator panel',
    bind: 'Organization {{organization}} bound to client {{tenant}}',
    revokeBinding: 'Binding between {{organization}} and client {{tenant}} revoked',
    restoreBinding: 'Binding between {{organization}} and client {{tenant}} restored',
    pagesProject: 'Cloudflare Pages project of site {{site}}',
    pagesDomain: '{{domain}} attached to the Pages project',
  },
}
